import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { adminAPI } from '../services/api';

const NAV = [
  { icon: Home2Icon,   label: 'Home',           path: '/' },
  { icon: HistoryIcon, label: 'Query History',   path: '/history' },
  { icon: BookmarkIcon,label: 'Saved Queries',   path: '/saved' },
  { icon: SchemaIcon,  label: 'Schema Explorer', path: '/schema' },
  { icon: SettingsIcon,label: 'Settings',        path: '/settings' },
];

export default function Sidebar() {
  const [mounted, setMounted] = useState(false);
  const [dbName, setDbName]   = useState('');
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    adminAPI.dbConfig.get()
      .then(r => setDbName(r.data?.config?.database || ''))
      .catch(() => {});
  }, []);

  return (
    <aside style={{
      ...S.sidebar,
      transform: mounted ? 'translateX(0)' : 'translateX(-100%)',
      opacity: mounted ? 1 : 0,
      transition: 'transform 0.45s cubic-bezier(0.175,0.885,0.32,1.05), opacity 0.4s ease'
    }}>
      {/* ── Logo ── */}
      <div style={{ ...S.logo, animation: 'fadeInLeft 0.4s ease 0.2s both' }}>
        <div style={S.logoInner}>
          <img
            src="/logo.svg"
            alt="SolutionOne"
            style={{
              width: 90, height: 'auto', objectFit: 'contain',
              filter: 'drop-shadow(0 2px 10px rgba(27,94,59,0.28))',
              animation: 'sidebarLogoSpin 0.9s cubic-bezier(0.175,0.885,0.32,1.275) 0.25s both'
            }}
          />
          <div style={S.logoTag}>Intelligence Platform</div>
        </div>
        {/* Gradient accent line */}
        <div style={S.logoDivider} />
      </div>

      <style>{`
        @keyframes sidebarLogoSpin {
          from { opacity:0; transform: scale(0.6); }
          to   { opacity:1; transform: scale(1); }
        }
      `}</style>

      {/* ── Nav ── */}
      <nav style={S.nav}>
        {NAV.map(({ icon: Icon, label, path }, i) => {
          const active = location.pathname === path;
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              className="nav-item"
              style={{
                ...S.navItem,
                ...(active ? S.navItemActive : {}),
                animation: `fadeInLeft 0.35s ease ${0.25 + i * 0.07}s both`
              }}
            >
              {active && <div style={S.activeBar} />}
              <Icon active={active} />
              <span style={{ flex: 1 }}>{label}</span>
              {active && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#1B5E3B' }} />}
            </button>
          );
        })}

        {user?.role === 'admin' && (
          <>
            <div style={S.divider}><span style={S.dividerLabel}>Admin</span></div>
            <button
              onClick={() => navigate('/admin')}
              className="nav-item"
              style={{
                ...S.navItem,
                ...(location.pathname.startsWith('/admin') ? S.navItemAdmin : {}),
                animation: 'fadeInLeft 0.35s ease 0.6s both'
              }}
            >
              {location.pathname.startsWith('/admin') && <div style={{ ...S.activeBar, background: 'linear-gradient(180deg, #b45309, #f59e0b)' }} />}
              <ShieldIcon active={location.pathname.startsWith('/admin')} />
              <span style={{ flex: 1 }}>Admin Panel</span>
              <span style={S.adminBadge}>Admin</span>
            </button>
          </>
        )}
      </nav>

      {/* ── DB Status card ── */}
      <div style={{ ...S.dbCard, animation: 'fadeInUp 0.4s ease 0.65s both' }}>
        <div style={S.dbHeader}>
          <span style={S.dbLabel}>Database</span>
          <span style={S.dbStatus}>
            <span style={S.dbDot} />
            Connected
          </span>
        </div>
        <div style={S.dbRow}>
          <span style={S.dbKey}>Name</span>
          <span style={S.dbVal}>{dbName || '—'}</span>
        </div>
        <div style={S.dbRow}>
          <span style={S.dbKey}>Engine</span>
          <span style={S.dbVal}>SQL Server 2022</span>
        </div>
        <div style={S.dbRow}>
          <span style={S.dbKey}>AI Model</span>
          <span style={{ ...S.dbVal, color: '#1B5E3B', fontWeight: 500 }}>GPT-4o</span>
        </div>
      </div>

      {/* ── User row ── */}
      <div style={{ ...S.userRow, animation: 'fadeInUp 0.4s ease 0.75s both' }}>
        <div style={S.avatar}>
          {user?.name?.[0]?.toUpperCase() || 'U'}
        </div>
        <div style={S.userInfo}>
          <div style={S.userName}>{user?.name || 'User'}</div>
          <div style={S.userEmail}>{user?.email || ''}</div>
        </div>
        <button onClick={logout} style={S.logoutBtn} title="Sign out">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </button>
      </div>
    </aside>
  );
}

// ── Inline SVG icon components ─────────────────────────────────────────────

function Home2Icon({ active }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={active ? '#1B5E3B' : '#9b9b9b'} strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  );
}
function HistoryIcon({ active }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={active ? '#1B5E3B' : '#9b9b9b'} strokeWidth="2">
      <polyline points="12 8 12 12 14 14"/>
      <path d="M3.05 11a9 9 0 1 0 .5-4.5"/>
      <polyline points="3 3 3 9 9 9"/>
    </svg>
  );
}
function BookmarkIcon({ active }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={active ? '#1B5E3B' : '#9b9b9b'} strokeWidth="2">
      <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
    </svg>
  );
}
function SchemaIcon({ active }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={active ? '#1B5E3B' : '#9b9b9b'} strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="3" y1="15" x2="21" y2="15"/>
      <line x1="9" y1="3" x2="9" y2="21"/>
    </svg>
  );
}
function ShieldIcon({ active }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={active ? '#b45309' : '#9b9b9b'} strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}
function SettingsIcon({ active }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={active ? '#1B5E3B' : '#9b9b9b'} strokeWidth="2">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────

const S = {
  sidebar: {
    width: 240, minWidth: 240, height: '100vh',
    background: '#fff',
    borderRight: '1px solid #cce8d8',
    display: 'flex', flexDirection: 'column',
    position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 10,
    boxShadow: '2px 0 20px rgba(0,0,0,0.04)'
  },
  logo: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center',
    padding: '14px 12px 0',
  },
  logoInner: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
    paddingBottom: 12,
  },
  logoTag: {
    fontSize: 9, fontWeight: 700, letterSpacing: '1.2px',
    textTransform: 'uppercase', color: '#81C784',
    background: 'linear-gradient(135deg, #E8F6EE, #D1F0E0)',
    border: '1px solid #C8EDD6',
    borderRadius: 20, padding: '2px 10px',
  },
  logoDivider: {
    width: '100%', height: 1,
    background: 'linear-gradient(90deg, transparent, rgba(27,94,59,0.18), transparent)',
  },
  nav: { padding: '10px 8px', flex: 1, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' },
  divider: { display: 'flex', alignItems: 'center', padding: '10px 8px 4px', gap: 8 },
  dividerLabel: { fontSize: 10, fontWeight: 700, color: '#c0bdb8', letterSpacing: '0.8px', textTransform: 'uppercase' },
  navItemAdmin: {
    background: 'linear-gradient(135deg, rgba(180,83,9,0.08), rgba(245,158,11,0.06))',
    color: '#b45309', fontWeight: 600
  },
  adminBadge: {
    fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
    background: 'linear-gradient(135deg, #b45309, #f59e0b)', color: '#fff',
    letterSpacing: '0.3px'
  },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 10, position: 'relative',
    padding: '9px 10px', borderRadius: 8, border: 'none',
    background: 'transparent', color: '#6b6b6b', fontSize: 13,
    width: '100%', textAlign: 'left', cursor: 'pointer'
  },
  navItemActive: {
    background: 'linear-gradient(135deg, rgba(27,94,59,0.1), rgba(46,125,82,0.07))',
    color: '#1B5E3B', fontWeight: 600
  },
  activeBar: {
    position: 'absolute', left: 0, top: '20%', bottom: '20%',
    width: 3, borderRadius: '0 3px 3px 0',
    background: 'linear-gradient(180deg, #1B5E3B, #2E7D52)'
  },
  dbCard: {
    margin: '0 12px 12px', padding: '14px',
    background: 'linear-gradient(135deg, #f2faf8, #E8F6EE)',
    borderRadius: 12, border: '1px solid #cce8d8', fontSize: 12
  },
  dbHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 10
  },
  dbLabel: { fontWeight: 600, color: '#1a1a1a', fontSize: 12 },
  dbStatus: {
    display: 'flex', alignItems: 'center', gap: 5,
    color: '#059669', fontSize: 11, fontWeight: 500
  },
  dbDot: {
    width: 7, height: 7, borderRadius: '50%', background: '#059669',
    boxShadow: '0 0 6px rgba(5,150,105,0.6)',
    animation: 'pulse 2s ease-in-out infinite'
  },
  dbRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginTop: 6
  },
  dbKey: { color: '#9b9b9b', fontSize: 11 },
  dbVal: { color: '#1a1a1a', fontSize: 11, fontWeight: 500 },
  userRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '12px 14px', borderTop: '1px solid #e4f5ed'
  },
  avatar: {
    width: 32, height: 32, borderRadius: '50%',
    background: 'linear-gradient(135deg, #0A2E17 0%, #1B5E3B 40%, #2E7D52 100%)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, flexShrink: 0,
    boxShadow: '0 2px 8px rgba(27,94,59,0.35), inset 0 1px 0 rgba(255,255,255,0.15)'
  },
  userInfo: { flex: 1, minWidth: 0 },
  userName:  { fontSize: 13, fontWeight: 600, color: '#1a1a1a' },
  userEmail: { fontSize: 11, color: '#9b9b9b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  logoutBtn: {
    background: 'none', border: 'none', color: '#c0bdb8',
    padding: 6, borderRadius: 6, cursor: 'pointer',
    transition: 'color 0.15s, background 0.15s',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  }
};
